/**
 * @file services/firebase.js
 * @description Firebase initialisation, Auth helpers, and Firestore CRUD helpers.
 * Replace the firebaseConfig object with your own project credentials from
 * https://console.firebase.google.com → Project Settings → Your apps.
 *
 * Collections used:
 *   users/{uid}/meals  – individual meal log documents
 */

import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

// ── 🔥 Replace with your Firebase project config ──────────────────────────
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};
// ──────────────────────────────────────────────────────────────────────────

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const db = getFirestore(app);

// ── Auth ──────────────────────────────────────────────────────────────────

/**
 * Register a new user with email + password and optional display name.
 * @param {string} email
 * @param {string} password
 * @param {string} [displayName]
 * @returns {Promise<import("firebase/auth").UserCredential>}
 */
export async function registerUser(email, password, displayName) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) await updateProfile(cred.user, { displayName });
  return cred;
}

/**
 * Sign in an existing user.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<import("firebase/auth").UserCredential>}
 */
export async function loginUser(email, password) {
  return signInWithEmailAndPassword(auth, email, password);
}

/**
 * Sign in using Google Provider.
 * @returns {Promise<import("firebase/auth").UserCredential>}
 */
export async function loginWithGoogle() {
  const provider = new GoogleAuthProvider();
  return signInWithPopup(auth, provider);
}

/**
 * Sign out the current user.
 * @returns {Promise<void>}
 */
export async function logoutUser() {
  return signOut(auth);
}

/**
 * Send a password reset email.
 * @param {string} email
 * @returns {Promise<void>}
 */
export async function resetPassword(email) {
  return sendPasswordResetEmail(auth, email);
}

/**
 * Subscribe to auth state changes.
 * @param {(user: import("firebase/auth").User|null) => void} callback
 * @returns {() => void} unsubscribe function
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

// ── Meals CRUD ────────────────────────────────────────────────────────────

/**
 * Add a meal log entry for the current user.
 * @param {string} uid  – Firebase user UID
 * @param {{ description: string, calories: number, protein: number, source: string }} mealData
 * @returns {Promise<import("firebase/firestore").DocumentReference>}
 */
export async function addMeal(uid, mealData, createdAt = serverTimestamp()) {
  const ref = collection(db, "users", uid, "meals");
  return addDoc(ref, {
    ...mealData,
    createdAt: mealData?.date || createdAt, // Use provided timestamp or server timestamp
  }).catch((e) => {
    console.error("Error adding meal:", e);
    throw e;
  });
}

/**
 * Fetch all meal logs for a user, ordered newest first.
 * @param {string} uid
 * @returns {Promise<Array<{ id: string, description: string, calories: number, protein: number, createdAt: any }>>}
 */
export async function getMeals(uid) {
  const ref = collection(db, "users", uid, "meals");
  const q = query(ref, orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Delete a meal log entry.
 * @param {string} uid
 * @param {string} mealId
 * @returns {Promise<void>}
 */
export async function deleteMeal(uid, mealId) {
  return deleteDoc(doc(db, "users", uid, "meals", mealId));
}

/**
 * Update an existing meal log entry.
 * @param {string} uid – Firebase user UID
 * @param {string} mealId – The ID of the meal document to update
 * @param {Partial<{ description: string, calories: number, protein: number, source: string }>} mealData
 * @returns {Promise<void>}
 */
export async function updateMeal(uid, mealId, mealData) {
  const mealRef = doc(db, "users", uid, "meals", mealId);

  try {
    return await updateDoc(mealRef, {
      ...mealData,
      updatedAt: serverTimestamp(), // Optional: track when it was last edited
    });
  } catch (e) {
    console.error("Error updating meal:", e);
    throw e;
  }
}
// ── Profile CRUD ──────────────────────────────────────────────────────────

/**
 * Save or update user physical metrics.
 */
export async function updateProfileData(uid, data) {
  const ref = doc(db, "users", uid, "config", "profile");
  return setDoc(ref, data, { merge: true });
}

/**
 * Fetch user physical metrics.
 */
export async function getProfileData(uid) {
  const ref = doc(db, "users", uid, "config", "profile");
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}
